/**
 * HI_LO (craps) — simultaneous single-die rolls to pick the shooter
 * (called only at game start in the new flow; subsequent rounds
 * round-robin through `shooterQueue`).
 *
 * Mirrors cee-lo's HiLoState exactly; only the instruction copy
 * differs ("banker" → "shooter").
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';
import { HiLoAnims } from '../../../config/AnimationConfig';

export class CrapsHiLoState extends State {
  private lastSeenRoundsLength = 0;

  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    this.lastSeenRoundsLength = update.hiLoRounds?.length ?? 0;
    this._resetRollSetup();

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('hi_lo');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();

    // HI_LO is single-die — defensively pin every opponent's
    // activeDiceCount to 1 so the upcoming dice:data drag handler
    // pulls only one die into the drag group. Craps's default is 2
    // (loadOpponent's MAX_DICE_COUNT or a trailing throw setting it
    // from diceStates.length), and applyPerUpdateBookkeeping's
    // bookkeeping isn't a guarantee for late opponents that load
    // after this state's onEnter ran. Cee-lo's HiLoState does the
    // same.
    const opp = game.opponentDiceManager;
    if (opp) {
      for (const id of update.players?.map(p => p.playerId) ?? []) {
        opp.setActiveDiceCount(id, 1);
      }
    }

    await game.exitBettingMode({
      animated: update.animated,
      previousState: update.previousState,
    });

    this.applyInstruction(update);
    this._updateProfiles(update);

    // Authorization for HI_LO rides the controller's follow-up broadcast (it
    // runs post-transition), so this usually fires from onUpdate. The keyed
    // guard re-spawns on a tie re-roll (hiLoRounds grows) but never twice for
    // the same round.
    this._maybeSetupLocalRoll(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    const game = this.game;

    const rounds = update.hiLoRounds?.length ?? 0;
    const newRound = rounds > this.lastSeenRoundsLength;
    if (newRound) {
      game.clearResultDedup();
      this.lastSeenRoundsLength = rounds;
      // Re-arm the roll-setup gate from the server-authoritative round counter
      // (tie re-roll) instead of trusting the deauth→reauth edge, which can be
      // missed across back-to-back broadcasts and strand the tied player. See
      // HiLoState for the full rationale.
      this._resetRollSetup();
    }

    if (update.lastRollResult) {
      game.handleNewRollResult(update.lastRollResult);
    }

    this._updateProfiles(update);
    this.applyInstruction(update);

    this._maybeSetupLocalRoll(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    // Same explicit chain as cee-lo HiLoState.onExit — see that
    // file for the full rationale on the explicit choreography here.
    const game = this.game;
    const overlay = game.playerProfilesOverlay;
    if (!overlay) return;
    if (!update.animated) return;

    await overlay.waitForAllFlyouts();

    // CrapsStrategy wires bankerId = shooterId post-HI_LO; same
    // primitive as cee-lo, no game branch.
    const winnerId = update.bankerId;
    if (winnerId) {
      const screenPos = game.getDiceCenterScreen(winnerId);
      if (screenPos) {
        await overlay.flyoutBankIcon(
          winnerId,
          screenPos,
          HiLoAnims.winnerRevealDuration + 0.3,
        );
      }
    }

    overlay.clearAllDiceResults(true);
    game.clearResultDedup();

    await game.delay(300);

    // Gear-out for losers only — winner keeps their gear visible
    // throughout, no disappear/reappear flicker.
    await overlay.animateDiceGearOut(HiLoAnims.diceGearOutDuration, winnerId);

    this._updateProfiles(update);

    await game.delay(500);

    // Spawn the first round's banker zone now — same idea as the
    // POINT_ROLL / COME_OUT onExit calls: get the zone into 3D scene
    // before SHOOTER_SET_STACK.onEnter kicks off the camera lerp, so
    // the camera passes over a visible zone instead of empty floor.
    game.primeBetZones();

    // Dice cleanup is owned by the destination state's
    // enterBettingMode (SHOOTER_SET_STACK) — fades dice off the
    // table in lock-step with the camera lerp instead of in a
    // separate beat here.
  }

  private applyInstruction(update: GameUpdate): void {
    const localId = this.game.getLocalPlayerId();
    const contenders = update.hiLoContenders ?? [];
    const rolls = update.hiLoRolls ?? {};
    const isContender = !!localId && contenders.includes(localId);
    const hasRolled = !!localId && Object.prototype.hasOwnProperty.call(rolls, localId);
    const isReroll = (update.hiLoRounds?.length ?? 0) > 0;
    const banner = this.game.instructionBanner;
    if (!banner) return;
    if (isContender && !hasRolled) {
      banner.show({
        title: 'Hi-Lo',
        text: isReroll ? 'Re-roll to break the tie' : 'Roll to determine the shooter',
      }, update.animated);
    } else {
      banner.show({
        title: 'Hi-Lo',
        text: isReroll ? 'Players are re-rolling to break the tie…' : 'Players are rolling for shooter…',
      }, update.animated);
    }
  }
}
